import { useEffect, useMemo } from "react";
import type { CaptionAuthoredStyle } from "@desk/core";
import type { DeskSession } from "../hooks/useDeskSession";
import { isGrammarValid, nextOpId } from "../lib/edit-helpers";
import { packEndTick } from "../lib/caption-preview";
import { SHARED_REACTION_TAGS } from "../../content/creative-catalogs.js";
import type { HeadlineFraming } from "../../content/creative-catalogs.js";
import {
  headlineFramingsForPack,
  matchFramingFromTokens,
} from "../lib/headline-remix.js";
import { DEFAULT_CAPTION_STYLE } from "../lib/caption-style-options.js";
import { coachLineFor } from "./tools.js";
import { ToolCartButton } from "./ToolCartButton.js";

/**
 * Caption tool — a HEADLINE FRAMING picker (not a word-by-word tile builder).
 * Full preset headlines spin the SAME footage differently; each carries a risk
 * cue (SPIN = deniable angle vs CLAIM = checkable fact). The caption STYLE
 * (plain / minimal / bouncy meme / breaking-news — a real caption style that
 * both rescores the graph and moves "How it's landing") is picked with the
 * inline ‹ prev · name · next › switcher ON the video (CaptionOverlay), not
 * here. The caption renders directly on the video preview (that IS the
 * preview) and can be dragged there to reposition it.
 */

export function TextTool({
  session,
  reactionId = "reaction-none",
  onReactionChange,
}: {
  session: DeskSession;
  reactionId?: string;
  onReactionChange?: (id: string) => void;
}) {
  const { pack, applyOp, resolved } = session;
  const sourceEnd = packEndTick(pack);

  const framings = useMemo(() => headlineFramingsForPack(pack), [pack]);
  const lastCaption = resolved.captions[resolved.captions.length - 1];
  const tokenIds = lastCaption?.tokenIds ?? [];
  const selectedFraming = useMemo(
    () => matchFramingFromTokens(framings, tokenIds),
    [framings, tokenIds],
  );

  // The ACTIVE style is the resolved caption's own style when one exists (so a
  // restored/edited caption reads truthfully), else the default a fresh
  // headline starts with. Style is picked afterward via the on-video inline
  // switcher (CaptionOverlay), not here.
  const activeStyle: CaptionAuthoredStyle =
    (lastCaption?.style as CaptionAuthoredStyle) ?? DEFAULT_CAPTION_STYLE;

  const byId = useMemo(
    () => new Map(pack.captionVocabulary.tokens.map((t) => [t.id, t.text])),
    [pack],
  );
  const preview =
    tokenIds.length > 0
      ? tokenIds.map((id) => byId.get(id) ?? "?").join(" ")
      : "Pick a headline framing";

  // Keep local reaction state aligned when the pack resets upstream.
  useEffect(() => {
    onReactionChange?.("reaction-none");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack.id]);

  const emitCaption = (framingTokenIds: string[], style: CaptionAuthoredStyle) => {
    if (!isGrammarValid(pack, framingTokenIds)) return;
    const end = Math.max(1, sourceEnd || 90);
    applyOp({
      kind: "text",
      opId: nextOpId("text"),
      captionId: lastCaption?.captionId ?? nextOpId("cap"),
      tokenIds: framingTokenIds,
      startTick: 0,
      endTick: end,
      style,
      revealMode: "static",
      emphasizedTokenIds: [],
    });
  };

  const applyFraming = (framing: HeadlineFraming) => emitCaption(framing.tokenIds, activeStyle);

  return (
    <div className="tool-panel kid-tool caption-tool" data-testid="text-tool">
      <ToolCartButton tool="text" />
      <h3>Caption</h3>
      <p className="coach-line" data-testid="coach-line">
        {coachLineFor("text")}
      </p>

      {/* The headline — full preset framings, each with a plain-language risk cue. */}
      <section className="headline-framing" data-testid="headline-framing-section">
        <span className="section-heading" id="headline-framing-heading">
          The headline
        </span>
        <div
          className="headline-card-grid"
          data-testid="headline-cards"
          role="group"
          aria-labelledby="headline-framing-heading"
        >
          {framings.map((framing) => {
            const selected = selectedFraming?.id === framing.id;
            return (
              <div
                key={framing.id}
                className={`headline-card${selected ? " active" : ""}`}
                data-testid={`headline-card-${framing.id}`}
                data-risk={framing.risk}
              >
                <button
                  type="button"
                  className="headline-card-main"
                  data-testid={`headline-main-${framing.id}`}
                  aria-pressed={selected}
                  aria-label={framing.text}
                  onClick={() => applyFraming(framing)}
                >
                  <strong className="headline-card-sentence">{framing.text}</strong>
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Reaction tag — cosmetic emoji stuck under the headline on the reel. */}
      <section className="reaction-tag-section" data-testid="reaction-tag-section">
        <span className="section-heading reaction-tag-heading" id="reaction-tag-heading">
          Reaction tag
        </span>
        <div
          className="reaction-tag-grid"
          data-testid="reaction-tag-row"
          role="group"
          aria-labelledby="reaction-tag-heading"
        >
          {SHARED_REACTION_TAGS.map((r) => {
            const active = r.id === reactionId;
            return (
              <button
                key={r.id}
                type="button"
                className={`reaction-tag-chip${active ? " active" : ""}`}
                data-testid={`reaction-tag-${r.id}`}
                data-tone={r.tone}
                aria-pressed={active}
                aria-label={r.label}
                title={r.label}
                onClick={() => onReactionChange?.(r.id)}
              >
                <span aria-hidden>{r.emoji}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Accessible echo of the live caption (rendered on the video preview). */}
      <p className="caption-preview sr-only" data-testid="caption-preview">
        {preview}
      </p>
    </div>
  );
}
